/* ==========================================================================
   ST hero video activation
   ---------------------------------------------------------------------------
   Ella's "Video Slider" block renders <source data-src="..."> with no src and
   ships no script that resolves it, so the mp4 slide never plays. lazysizes
   only handles elements carrying the `lazyload` class, and this markup uses
   `lazyVdHide`, which nothing in the theme reads.

   This file resolves data-src -> src and starts playback, with three guards:
     1. Small viewports keep the poster still and never download the file
        (the current asset is ~78 MB).
     2. prefers-reduced-motion: reduce keeps the poster and offers manual play.
     3. Playback pauses when the hero scrolls out of view.

   Added by TRINIQ. Not part of Ella — safe to re-apply after a theme update.
   ========================================================================== */
(function () {
  'use strict';

  var MOBILE_MAX = 767;
  var VISIBLE_RATIO = 0.25;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isMobile(root) {
    var bp = parseInt(root.getAttribute('data-st-breakpoint'), 10) || MOBILE_MAX;
    return window.matchMedia('(max-width: ' + bp + 'px)').matches;
  }

  function setSource(video) {
    if (video.dataset.stResolved === 'true') return true;
    var source = video.querySelector('source[data-src]');
    if (!source) return false;
    var url = source.getAttribute('data-src');
    if (!url) return false;
    source.setAttribute('src', url);
    source.removeAttribute('data-src');
    video.classList.remove('lazyVdHide');
    video.dataset.stResolved = 'true';
    video.load();
    return true;
  }

  function play(root, video) {
    if (!setSource(video)) return;
    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      // Autoplay can still be refused (power saving, data saver). The poster
      // stays visible and the control lets the visitor start it by hand.
      attempt.catch(function () {
        root.classList.remove('is-playing');
        root.classList.add('is-interactive');
        markToggle(root, true);
      });
    }
  }

  function markToggle(root, paused) {
    var toggle = root.querySelector('[data-st-video-toggle]');
    if (!toggle) return;
    toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
    toggle.setAttribute('aria-label', paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
  }

  function wireToggle(root, video) {
    var toggle = root.querySelector('[data-st-video-toggle]');
    if (!toggle) return;
    toggle.addEventListener('click', function () {
      if (video.paused) {
        setSource(video);
        video.play();
      } else {
        video.pause();
      }
    });
    video.addEventListener('play', function () {
      root.classList.add('is-playing', 'is-interactive');
      markToggle(root, false);
    });
    video.addEventListener('pause', function () {
      markToggle(root, true);
    });
  }

  function posterOnly(root, offerControl) {
    root.classList.add('is-poster-only');
    // Reduced-motion visitors keep an opt-in play control. On phones the
    // video is deliberately never downloaded, so no control is offered.
    if (offerControl) root.classList.add('is-interactive');
    markToggle(root, true);
  }

  function init(root) {
    if (root.dataset.stInit === 'true') return;
    root.dataset.stInit = 'true';

    var video = root.querySelector('video');
    if (!video) return;

    wireToggle(root, video);

    var mobileBehaviour = root.getAttribute('data-st-mobile') || 'poster';
    if (isMobile(root) && mobileBehaviour !== 'play') {
      posterOnly(root, false);
      return;
    }

    if (prefersReducedMotion()) {
      posterOnly(root, true);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      play(root, video);
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.intersectionRatio >= VISIBLE_RATIO) {
          play(root, video);
        } else if (video.dataset.stResolved === 'true' && !video.paused) {
          video.pause();
        }
      });
    }, { threshold: [0, VISIBLE_RATIO] });

    observer.observe(root);
  }

  function boot() {
    var roots = document.querySelectorAll('[data-st-hero-video]');
    Array.prototype.forEach.call(roots, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // The theme editor re-renders sections without a full page load.
  document.addEventListener('shopify:section:load', boot);
})();
